import { useEffect, useRef, useState } from 'react'
import { Input, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconTemplate } from '@tabler/icons-react'

interface CreateProjectTemplateModalProps {
  modalRef: React.RefObject<HTMLDialogElement>
  /** Prefilled name, usually the project title */
  defaultName?: string
  onConfirm: (name: string) => void
  isLoading?: boolean
}

export function useCreateProjectTemplateModal() {
  const modalRef = useRef<HTMLDialogElement>(null)
  return {
    modalRef,
    show: () => modalRef.current?.showModal(),
    close: () => modalRef.current?.close(),
  }
}

export function CreateProjectTemplateModal({
  modalRef,
  defaultName = '',
  onConfirm,
  isLoading,
}: CreateProjectTemplateModalProps) {
  const [name, setName] = useState(defaultName)

  // The project title arrives asynchronously — keep the prefill in sync until the user types.
  useEffect(() => {
    setName(defaultName)
  }, [defaultName])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    onConfirm(name.trim())
  }

  return (
    <Modal ref={modalRef} className="bg-neutral">
      <Modal.Header>
        <div className="flex flex-col items-center gap-3 pt-2">
          <div className="bg-primary bg-opacity-20 text-primary rounded-full p-4">
            <IconTemplate size={36} />
          </div>
          <span className="text-xl font-bold text-center">Vorlage aus diesem Projekt erstellen</span>
        </div>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="form-control w-full">
            <div className="label">
              <span className="label-text">Name der Vorlage</span>
            </div>
            <Input
              placeholder="z.B. Hochzeit Standard"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
            />
            <div className="label">
              <span className="label-text-alt opacity-60">
                Design, Farben, Schriften, Auswahl-Funktionen, Wasserzeichen, Preisliste, Marke und Tags dieses Projekts
                werden in die Vorlage übernommen.
              </span>
            </div>
          </label>
          <div className="flex flex-row gap-3 justify-center">
            <Button type="button" color="ghost" disabled={isLoading} onClick={() => modalRef.current?.close()}>
              Abbrechen
            </Button>
            <Button type="submit" color="primary" loading={isLoading} disabled={isLoading || !name.trim()}>
              Erstellen
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}
